import {
  Form,
  FormSubmitButton,
  Slideover,
  TextAreaFormField,
} from "@/components/ui";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import {
  retrieveMatterQueryOptions,
  useUpdateMatter,
} from "@/lib/data/react-query/matters/matter";
import { MatterSchema } from "@/lib/data/schemas/matters/MatterSchema";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSuspenseQuery } from "@tanstack/react-query";
import { Info } from "lucide-react";
import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { type z } from "zod";

interface MatterCriticalInfoSlideoverProps {
  matterId: string;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
}

const formSchema = MatterSchema.pick({
  criticalInfo: true,
});

const defaultFormValues = {
  criticalInfo: "",
};

export const MatterCriticalInfoSlideover = ({
  matterId,
  isOpen,
  setIsOpen,
}: MatterCriticalInfoSlideoverProps) => {
  const { data: matterData } = useSuspenseQuery(
    retrieveMatterQueryOptions(matterId),
  );
  const matter = matterData.data;

  const updateMatter = useUpdateMatter();

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    values: {
      ...defaultFormValues,
      ...matter,
    },
    mode: "onSubmit",
  });

  const handleSubmit = (data: z.infer<typeof formSchema>) => {
    updateMatter.mutate({
      criticalInfo: data.criticalInfo,
      dateModified: matter.dateModified,
      id: matter.id,
    });
    setIsOpen(false);
  };

  useEffect(() => {
    if (!isOpen) {
      form.reset();
    }
  }, [isOpen, form]);

  return (
    <Slideover
      title="Critical matter information"
      titleIcon={<Info className="h-4 w-4" />}
      open={isOpen}
      setOpen={setIsOpen}
      isDirty={form.formState.isDirty}
    >
      <Form {...form}>
        <form
          onSubmit={form.handleSubmit(handleSubmit)}
          className="mt-4 flex flex-col gap-4 p-6"
        >
          <fieldset className="grid grid-cols-1 gap-4">
            <TextAreaFormField
              register={form.register("criticalInfo")}
              label="Critical matter information"
              className="col-span-full"
              textAreaClassName="field-sizing-content max-h-[20lh]"
            />
          </fieldset>
          <div className="flex justify-end gap-2">
            <FormCancelButton
              setOpen={setIsOpen}
              isDirty={form.formState.isDirty}
            />
            <FormSubmitButton loading={updateMatter.isPending} />
          </div>
        </form>
      </Form>
    </Slideover>
  );
};
